import { useMemo, useState } from "react";
import { Alert, Button, DataTable, DetailList, Field, Pagination } from "../../../ui";
import type { DataColumn } from "../../../ui";
import type { Schemas } from "../../workspace/api.generated";
import { businessCodeLabel, date, money } from "../../workspace/model";
import { FilterBar, PageHeader, Panel, RowLink, StateNotice, statusBadge } from "../../workspace/shared";
import type { IssueStatusFilter } from "../schema";

type QueryState = "ready" | "loading" | "error" | "not-found";
type DetailQueryState = QueryState | "denied";

export interface ReconciliationRunDetailViewProps {
  readonly runId: number;
  readonly run?: Schemas["ReconciliationRunView"];
  readonly runState: DetailQueryState;
  readonly issues?: Schemas["AdminPageReconciliationIssueView"];
  readonly issuesState: Exclude<QueryState, "not-found">;
  readonly issueStatus: IssueStatusFilter;
  readonly backHref: string;
  readonly backLabel: string;
  readonly onBack: () => void;
  readonly issueHref: (issueId: number) => string;
  readonly onOpenIssue: (issue: Schemas["ReconciliationIssueView"]) => void;
  readonly onIssueStatusChange: (status: IssueStatusFilter) => void;
  readonly onPageChange: (page: number) => void;
  readonly onRetryRun: () => void;
  readonly onRetryIssues: () => void;
}

export function ReconciliationRunDetailView(props: ReconciliationRunDetailViewProps) {
  const [statusDraft, setStatusDraft] = useState(props.issueStatus);
  const columns = useMemo(() => [
    {
      key: "id",
      title: "이슈 ID",
      sticky: "start" as const,
      render: (row: Schemas["ReconciliationIssueView"]) => (
        <RowLink
          href={props.issueHref(row.id)}
          triggerKey={`reconciliation-issue:${row.id}:detail`}
          onNavigate={() => props.onOpenIssue(row)}
        >
          {row.id}
        </RowLink>
      ),
    },
    { key: "type", title: "불일치 유형", render: (row: Schemas["ReconciliationIssueView"]) => businessCodeLabel(row.issueType) },
    { key: "ref", title: "업무 대상", render: (row: Schemas["ReconciliationIssueView"]) => row.businessRefType ? `${businessCodeLabel(row.businessRefType)} ${row.businessRefId ?? "—"}` : "—" },
    { key: "internal", title: "내부 금액", numeric: true, render: (row: Schemas["ReconciliationIssueView"]) => money(row.internalAmount) },
    { key: "provider", title: "Provider 금액", numeric: true, render: (row: Schemas["ReconciliationIssueView"]) => money(row.providerAmount) },
    { key: "status", title: "상태", render: (row: Schemas["ReconciliationIssueView"]) => statusBadge(row.status) },
  ] satisfies readonly DataColumn<Schemas["ReconciliationIssueView"]>[], [props]);

  return <>
    <PageHeader
      id="ADM-S30"
      title={props.run ? `${props.run.runNo} 대사 실행` : `대사 실행 ${props.runId}`}
      description="서버가 계산한 실행 집계와 이 실행에 속한 이슈만 확인합니다."
      breadcrumbs={[
        { label: "대사 관리", href: "/operations/reconciliations?tab=runs" },
        { label: "실행 상세", href: `/operations/reconciliations/${props.runId}` },
      ]}
      backAction={{ label: props.backLabel, href: props.backHref, onClick: props.onBack }}
    />

    {props.runState === "loading" ? <p role="status">대사 실행을 불러오는 중입니다.</p>
      : props.runState === "denied" ? <StateNotice state="denied" />
        : props.runState === "not-found" ? <StateNotice state="not-found" />
        : props.runState === "error" ? <Alert persistent variant="danger" title="대사 실행을 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={props.onRetryRun}>다시 조회</Button>}>실행 번호와 최신 상태를 다시 확인해 주세요.</Alert>
          : props.run ? <Panel title="실행 요약" description={`서버 실행 ID ${props.run.id} · Provider 설정 ID ${props.run.providerConfigurationId}`}>
            <DetailList items={[
              { label: "기준일", value: props.run.businessDate },
              { label: "대사 유형", value: businessCodeLabel(props.run.reconciliationType) },
              { label: "상태", value: statusBadge(props.run.status) },
              { label: "확인 건수", value: props.run.checkedCount.toLocaleString("ko-KR") },
              { label: "일치 건수", value: props.run.matchedCount.toLocaleString("ko-KR") },
              { label: "불일치 건수", value: props.run.issueCount.toLocaleString("ko-KR") },
              { label: "시작일시", value: date(props.run.startedAt) },
              { label: "완료일시", value: date(props.run.completedAt) },
            ]} />
          </Panel> : null}

    {props.runState === "ready" ? <Panel title="이 실행의 이슈" description="URL의 실행 ID와 일치하는 이슈만 서버 페이지 단위로 조회합니다.">
      <FilterBar onSubmit={() => props.onIssueStatusChange(statusDraft)}>
        <Field compact control="select" label="이슈 상태" value={statusDraft} onChange={(event) => setStatusDraft(event.currentTarget.value as IssueStatusFilter)}>
          <option value="">전체</option><option value="OPEN">미해결</option><option value="RESOLVED">해결</option>
        </Field>
      </FilterBar>
      {props.issuesState === "error" ? <Alert persistent variant="danger" title="이 실행의 이슈를 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={props.onRetryIssues}>다시 조회</Button>}>실행 요약은 유지했습니다.</Alert> : (
        <>
          <DataTable caption={`${props.run?.runNo ?? props.runId} 소속 이슈`} columns={columns} rows={props.issues?.items ?? []} rowKey={(row) => row.id} state={props.issuesState} onRetry={props.onRetryIssues} />
          {props.issues ? <Pagination page={props.issues.page} size={props.issues.size} totalCount={props.issues.totalCount} onPageChange={props.onPageChange} /> : null}
        </>
      )}
    </Panel> : null}
  </>;
}
